import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";

const MODEL_STEP_ORDER = ["extract_strategy", "plan_audiences", "build_criteria", "assemble_audience", "label_audiences"];
const STEP_COLORS = [
	"#6366f1",
	"#8b5cf6",
	"#ec4899",
	"#f59e0b",
	"#10b981",
	"#0ea5e9",
	"#14b8a6",
	"#a855f7",
];
const STEP_LABELS = {
	step1_extract: "Extract Strategy",
	step2_plan: "Plan Audiences",
	step3_build: "Build Criteria",
	step4_assemble: "Assemble",
	post_processing: "Post-processing",
	step6_sizing_prepare: "Sizing — Prepare",
	step7_optimize: "Sizing — Optimize",
	step8_diversify: "Sizing — Diversify",
	step6_sizing: "Sizing",
	step7_label: "Label Audiences",
};

function Section({ title, sectionKey, defaultOpen, children, count }) {
	const [open, setOpen] = useState(defaultOpen || false);
	const [showRaw, setShowRaw] = useState(false);
	const [rawData, setRawData] = useState(null);

	return (
		<div className="debug-collapsible">
			<button className="debug-collapsible-header" onClick={() => setOpen((o) => !o)}>
				<span className={`chevron ${open ? "open" : ""}`}>&#9654;</span>
				{title}
				{count != null && <span className="section-count">{count}</span>}
			</button>
			{open && (
				<div className="debug-section-body">
					{rawData !== null && (
						<div className="raw-json-toggle-bar">
							<button
								className="raw-json-toggle"
								onClick={() => setShowRaw((s) => !s)}
							>
								{showRaw ? "Structured View" : "Show Raw JSON"}
							</button>
						</div>
					)}
					{showRaw ? (
						<pre className="debug-json-block">{JSON.stringify(rawData, null, 2)}</pre>
					) : (
						<SectionRawSetter setRawData={setRawData}>{children}</SectionRawSetter>
					)}
				</div>
			)}
		</div>
	);
}

Section.propTypes = {
	title: PropTypes.string.isRequired,
	sectionKey: PropTypes.string.isRequired,
	defaultOpen: PropTypes.bool,
	children: PropTypes.node,
	count: PropTypes.number,
};

const RawDataContext = React.createContext(null);

function SectionRawSetter({ setRawData, children }) {
	return <RawDataContext.Provider value={setRawData}>{children}</RawDataContext.Provider>;
}

SectionRawSetter.propTypes = {
	setRawData: PropTypes.func.isRequired,
	children: PropTypes.node,
};

function useRegisterRaw(data) {
	const setRawData = React.useContext(RawDataContext);
	React.useEffect(() => {
		if (setRawData && data !== undefined) setRawData(data);
	}, [setRawData, data]);
}

/* ─── Timing Section ────────────────────────────────────────── */

function TimingSection({ timing }) {
	useRegisterRaw(timing);
	if (!timing) return null;

	// If granular sizing steps are present, drop the rollup `step6_sizing` to
	// avoid double-counting; otherwise (fallback path) the rollup is the only
	// sizing duration we have.
	const hasGranularSizing =
		timing.step6_sizing_prepare != null ||
		timing.step7_optimize != null ||
		timing.step8_diversify != null;
	const steps = Object.keys(STEP_LABELS).filter((k) => {
		if (timing[k] == null) return false;
		if (k === "step6_sizing" && hasGranularSizing) return false;
		return true;
	});
	const total = timing.total || steps.reduce((sum, k) => sum + (timing[k] || 0), 0);

	return (
		<div className="timing-content">
			<Tooltip id="debug-timing-tooltip" />
			<div className="timing-bar">
				{steps.map((key, i) => {
					const pct = total > 0 ? (timing[key] / total) * 100 : 0;
					if (pct < 0.5) return null;
					return (
						<div
							key={key}
							className="timing-bar-segment"
							style={{
								width: `${pct}%`,
								backgroundColor: STEP_COLORS[i % STEP_COLORS.length],
							}}
							data-tooltip-id="debug-timing-tooltip"
							data-tooltip-content={`${STEP_LABELS[key]}: ${formatMs(timing[key])} (${pct.toFixed(1)}%)`}
						/>
					);
				})}
			</div>
			<table className="timing-table">
				<thead>
					<tr>
						<th>Step</th>
						<th>Duration</th>
						<th>% of Total</th>
					</tr>
				</thead>
				<tbody>
					{steps.map((key, i) => (
						<tr key={key}>
							<td>
								<span
									className="timing-dot"
									style={{ backgroundColor: STEP_COLORS[i % STEP_COLORS.length] }}
								/>
								{STEP_LABELS[key]}
							</td>
							<td className="mono">{formatMs(timing[key])}</td>
							<td className="mono">
								{total > 0 ? ((timing[key] / total) * 100).toFixed(1) : 0}%
							</td>
						</tr>
					))}
				</tbody>
				<tfoot>
					<tr>
						<td>
							<strong>Total</strong>
						</td>
						<td className="mono">
							<strong>{formatMs(total)}</strong>
						</td>
						<td />
					</tr>
				</tfoot>
			</table>
		</div>
	);
}

TimingSection.propTypes = { timing: PropTypes.object };

/* ─── Request Config Section ────────────────────────────────── */

function RequestConfigSection({ config }) {
	useRegisterRaw(config);
	if (!config) return null;

	const models = config.models || {};
	const modelsUsed = config.models_used || {};
	const hasModelsUsed = Object.keys(modelsUsed).length > 0;
	const fallbackSteps = config.fallback_steps || [];

	return (
		<div className="config-grid">
			{config.silo && (
				<div className="config-row">
					<span className="config-label">Silo</span>
					<span className="config-value">{config.silo}</span>
				</div>
			)}
			{config.targeting_mode && (
				<div className="config-row">
					<span className="config-label">Targeting Mode</span>
					<span className="config-value">{config.targeting_mode}</span>
				</div>
			)}
			{config.temperature != null && (
				<div className="config-row">
					<span className="config-label">Temperature</span>
					<span className="config-value mono">{config.temperature}</span>
				</div>
			)}
			{config.environment && (
				<div className="config-row">
					<span className="config-label">Environment</span>
					<span className="config-value">{config.environment}</span>
				</div>
			)}
			{config.fallback_used != null && (
				<div className="config-row">
					<span className="config-label">Fallback Used</span>
					<span className="config-value">
						{config.fallback_used
							? fallbackSteps.length > 0
								? `yes — ${fallbackSteps.map(formatModelStep).join(", ")}`
								: "yes"
							: "no"}
					</span>
				</div>
			)}
			{Object.keys(models).length > 0 && (
				<>
					<div className="config-divider">Models (configured chain)</div>
					{MODEL_STEP_ORDER.filter((step) => models[step] != null).map((step) => (
						<div className="config-row" key={step}>
							<span className="config-label">{formatModelStep(step)}</span>
							<span className="config-value mono">{formatModelValue(models[step])}</span>
						</div>
					))}
				</>
			)}
			{hasModelsUsed && (
				<>
					<div className="config-divider">Models (actually used)</div>
					{MODEL_STEP_ORDER.filter((step) => modelsUsed[step] != null).map((step) => (
						<div className="config-row" key={step}>
							<span className="config-label">{formatModelStep(step)}</span>
							<span className="config-value mono">{formatModelValue(modelsUsed[step])}</span>
						</div>
					))}
				</>
			)}
		</div>
	);
}

RequestConfigSection.propTypes = { config: PropTypes.object };

/* ─── Step 1 — Strategy Extraction ──────────────────────────── */

function Step1Section({ extraction, prompt }) {
	useRegisterRaw(extraction);
	if (!extraction) return null;

	const fields = [
		["Requirements Summary", extraction.requirementsSummary],
		["Strategy Analysis", extraction.strategyAnalysis],
		["Audience Profile", extraction.audienceProfile],
		["Content Context", extraction.contentContext],
	];

	const [promptOpen, setPromptOpen] = useState(false);

	return (
		<div className="step1-content">
			{fields.map(
				([label, value]) =>
					value && (
						<div className="strategy-field" key={label}>
							<label>{label}</label>
							<p>{value}</p>
						</div>
					)
			)}

			{extraction.explicitSegments?.length > 0 && (
				<div className="strategy-field">
					<label>Explicit Segments</label>
					<div className="tag-list">
						{extraction.explicitSegments.map((seg, i) => (
							<span className="tag-pill" key={i}>
								{seg}
							</span>
						))}
					</div>
				</div>
			)}

			{extraction.geographicTargeting?.length > 0 && (
				<div className="strategy-field">
					<label>Geographic Targeting</label>
					<div className="tag-list">
						{extraction.geographicTargeting.map((geo, i) => (
							<span className="tag-pill geo" key={i}>
								{geo}
							</span>
						))}
					</div>
				</div>
			)}

			{prompt && (
				<div className="strategy-field">
					<button
						className="prompt-toggle"
						onClick={() => setPromptOpen((o) => !o)}
					>
						<span className={`chevron ${promptOpen ? "open" : ""}`}>&#9654;</span>
						Extraction Prompt
					</button>
					{promptOpen && <pre className="prompt-text">{prompt}</pre>}
				</div>
			)}
		</div>
	);
}

Step1Section.propTypes = {
	extraction: PropTypes.object,
	prompt: PropTypes.string,
};

/* ─── Step 2 — Audience Plan ────────────────────────────────── */

function Step2Section({ plan }) {
	useRegisterRaw(plan);
	if (!plan) return null;

	const audiences = plan.audiences || [];

	return (
		<div className="step2-content">
			<Tooltip id="grouping-strategy-tooltip" />
			{audiences.map((aud, i) => (
				<div className="audience-plan-card" key={i}>
					<div className="apc-header">
						<strong>{aud.name}</strong>
						{aud.groupingStrategy && (
							<span
								className="tag-pill strategy"
								data-tooltip-id="grouping-strategy-tooltip"
								data-tooltip-content={
									aud.groupingStrategy === "filter_layers"
										? "Each group is a targeting dimension (e.g. interests, location). Users must satisfy every group (AND logic)."
										: "Each group is a complete audience profile. Users only need to match one group (OR logic)."
								}
							>
								{aud.groupingStrategy === "filter_layers"
									? "Filter Layers"
									: "Alternative Profiles"}
							</span>
						)}
					</div>
					{aud.theme && <p className="apc-meta">{aud.theme}</p>}
					{aud.differentiation && (
						<p className="apc-meta muted">{aud.differentiation}</p>
					)}
					{aud.groups?.length > 0 && (
						<div className="apc-groups">
							{aud.groups.map((g, gi) => (
								<div className="apc-group" key={gi}>
									<span className="apc-group-label">{g.label}</span>
									{g.description && (
										<span className="apc-group-desc">
											&mdash; {g.description}
										</span>
									)}
									{g.criteriaTypes?.length > 0 && (
										<div className="tag-list small">
											{g.criteriaTypes.map((ct, ci) => (
												<span className="tag-pill small" key={ci}>
													{ct}
												</span>
											))}
										</div>
									)}
								</div>
							))}
						</div>
					)}
				</div>
			))}
		</div>
	);
}

Step2Section.propTypes = { plan: PropTypes.object };

/* ─── Step 3 — Criteria Data ────────────────────────────────── */

function Step3Section({ criteriaData, audienceNames }) {
	if (!criteriaData?.length) return null;

	return (
		<div className="step3-content">
			{criteriaData.map((item, i) => (
				<Step3Audience
					key={i}
					data={item}
					index={i}
					name={audienceNames?.[i]}
				/>
			))}
		</div>
	);
}

Step3Section.propTypes = {
	criteriaData: PropTypes.array,
	audienceNames: PropTypes.array,
};

function Step3Audience({ data, index, name }) {
	const [open, setOpen] = useState(false);
	const [showRaw, setShowRaw] = useState(false);

	const keywords = data.keywords || [];
	const categories = data.categories || [];
	const segments = data.segments || [];
	const taxonomy = data.taxonomy || [];
	const customCriteria = data.customCriteria || [];
	const customUserData = data.customUserData || [];
	const locations = data.locations || {};
	const demographics = data.demographics || {};
	const toolCalls = data.toolCalls || [];

	const counts = [];
	if (keywords.length) counts.push(`Keywords: ${keywords.length}`);
	if (categories.length) counts.push(`Categories: ${categories.length}`);
	if (segments.length) counts.push(`Segments: ${segments.length}`);
	if (taxonomy.length) counts.push(`Taxonomy: ${taxonomy.length}`);
	if (customCriteria.length) counts.push(`Custom Criteria: ${customCriteria.length}`);
	if (customUserData.length) counts.push(`Custom User Data: ${customUserData.length}`);
	if (toolCalls.length) counts.push(`Tool Calls: ${toolCalls.length}`);

	const title = name || `Audience ${index + 1}`;

	return (
		<div className="debug-collapsible nested">
			<button className="debug-collapsible-header" onClick={() => setOpen((o) => !o)}>
				<span className={`chevron ${open ? "open" : ""}`}>&#9654;</span>
				{title}
				{counts.length > 0 && (
					<span className="section-counts-inline">{counts.join(" | ")}</span>
				)}
			</button>
			{open && (
				<div className="debug-section-body">
					<div className="raw-json-toggle-bar">
						<button
							className="raw-json-toggle"
							onClick={() => setShowRaw((s) => !s)}
						>
							{showRaw ? "Structured View" : "Show Raw JSON"}
						</button>
					</div>
					{showRaw ? (
						<pre className="debug-json-block">
							{JSON.stringify(data, null, 2)}
						</pre>
					) : (
						<div className="step3-audience-content">
							<KeywordsTable keywords={keywords} />
							<CategoriesTable categories={categories} />
							<SegmentsTable segments={segments} />
							<TaxonomyTable taxonomy={taxonomy} />
							<CustomCriteriaTable customCriteria={customCriteria} />
							<CustomUserDataTable customUserData={customUserData} />
							<LocationsDisplay locations={locations} />
							<DemographicsDisplay demographics={demographics} />
							<ToolCallsLog toolCalls={toolCalls} />
						</div>
					)}
				</div>
			)}
		</div>
	);
}

Step3Audience.propTypes = {
	data: PropTypes.object.isRequired,
	index: PropTypes.number.isRequired,
	name: PropTypes.string,
};

function KeywordsTable({ keywords }) {
	if (!keywords.length) return null;
	const sorted = [...keywords].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Keywords ({keywords.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>Keyword</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((kw, i) => (
						<tr key={i}>
							<td>{kw.keyword || kw.name || kw.value}</td>
							<td className="mono">{(kw.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(kw.relevance ?? kw.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

KeywordsTable.propTypes = { keywords: PropTypes.array.isRequired };

function CategoriesTable({ categories }) {
	if (!categories.length) return null;
	const sorted = [...categories].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Categories ({categories.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>ID</th>
						<th>Category</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((cat, i) => (
						<tr key={i}>
							<td className="mono">{cat.id ?? cat.categoryId ?? ""}</td>
							<td>{cat.text || cat.category || cat.name || cat.label}</td>
							<td className="mono">{(cat.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(cat.relevance ?? cat.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

CategoriesTable.propTypes = { categories: PropTypes.array.isRequired };

function SegmentsTable({ segments }) {
	if (!segments.length) return null;
	const sorted = [...segments].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Segments ({segments.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>ID</th>
						<th>Title</th>
						<th>Provider</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((seg, i) => (
						<tr key={i}>
							<td className="mono">{seg.id ?? seg.segmentId ?? ""}</td>
							<td>{seg.title || seg.name}</td>
							<td>{seg.provider || seg.dataProvider || ""}</td>
							<td className="mono">{(seg.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(seg.relevance ?? seg.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

SegmentsTable.propTypes = { segments: PropTypes.array.isRequired };

function TaxonomyTable({ taxonomy }) {
	if (!taxonomy.length) return null;
	const sorted = [...taxonomy].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Taxonomy ({taxonomy.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>Property</th>
						<th>Value</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((item, i) => (
						<tr key={i}>
							<td>{item.property || item.key}</td>
							<td>{item.value || item.name}</td>
							<td className="mono">{(item.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(item.relevance ?? item.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

TaxonomyTable.propTypes = { taxonomy: PropTypes.array.isRequired };

function CustomCriteriaTable({ customCriteria }) {
	if (!customCriteria.length) return null;
	const sorted = [...customCriteria].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Custom Criteria ({customCriteria.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>Field</th>
						<th>Value</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((item, i) => (
						<tr key={i}>
							<td>{item.field || item.key}</td>
							<td>{item.value || item.name}</td>
							<td className="mono">{(item.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(item.relevance ?? item.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

CustomCriteriaTable.propTypes = { customCriteria: PropTypes.array.isRequired };

function CustomUserDataTable({ customUserData }) {
	if (!customUserData.length) return null;
	const sorted = [...customUserData].sort((a, b) => (b.relevance ?? b.relevanceScore ?? 0) - (a.relevance ?? a.relevanceScore ?? 0) || (b.pageViews || 0) - (a.pageViews || 0));
	return (
		<div className="criteria-subsection">
			<h5>Custom User Data ({customUserData.length})</h5>
			<table className="criteria-table">
				<thead>
					<tr>
						<th>Field</th>
						<th>Value</th>
						<th>Page Views</th>
						<th>Relevance</th>
					</tr>
				</thead>
				<tbody>
					{sorted.map((item, i) => (
						<tr key={i}>
							<td>{item.field || item.key}</td>
							<td>{item.value || item.name}</td>
							<td className="mono">{(item.pageViews ?? "").toLocaleString()}</td>
							<td className="mono">{formatRelevance(item.relevance ?? item.relevanceScore)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

CustomUserDataTable.propTypes = { customUserData: PropTypes.array.isRequired };

function LocationsDisplay({ locations }) {
	if (!locations || Object.keys(locations).length === 0) return null;

	const groups = [
		["Countries", locations.countries],
		["Regions", locations.regions],
		["Metros", locations.metros],
		["Cities", locations.cities],
	].filter(([, list]) => list?.length > 0);

	if (groups.length === 0) return null;

	return (
		<div className="criteria-subsection">
			<h5>Locations</h5>
			{groups.map(([label, items]) => (
				<div className="location-group" key={label}>
					<span className="location-group-label">{label}:</span>
					<div className="tag-list">
						{items.map((loc, i) => (
							<span className="tag-pill" key={i}>
								{typeof loc === "string" ? loc : loc.name || loc.label || loc.id}
							</span>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

LocationsDisplay.propTypes = { locations: PropTypes.object };

function DemographicsDisplay({ demographics }) {
	if (!demographics || Object.keys(demographics).length === 0) return null;

	const entries = Object.entries(demographics).filter(
		([, val]) => Array.isArray(val) && val.length > 0
	);
	if (entries.length === 0) return null;

	return (
		<div className="criteria-subsection">
			<h5>Demographics</h5>
			{entries.map(([key, values]) => (
				<div className="demo-group" key={key}>
					<span className="demo-group-label">{key}:</span>
					<div className="tag-list">
						{values.map((v, i) => (
							<span className="tag-pill demo" key={i}>
								{typeof v === "string" ? v : v.name || v.label || v.value}
							</span>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

DemographicsDisplay.propTypes = { demographics: PropTypes.object };

function ToolCallsLog({ toolCalls }) {
	if (!toolCalls.length) return null;
	return (
		<div className="criteria-subsection">
			<h5>Tool Calls ({toolCalls.length})</h5>
			<div className="tool-calls-log">
				{toolCalls.map((tc, i) => (
					<div className="tool-call-entry" key={i}>
						<span className="tag-pill tool">{tc.tool || tc.toolName || "tool"}</span>
						{(tc.args || tc.arguments) && (
							<code className="tool-call-args">
								{typeof (tc.args || tc.arguments) === "string"
									? tc.args || tc.arguments
									: JSON.stringify(tc.args || tc.arguments)}
							</code>
						)}
						{(tc.resultSummary || tc.result) && (
							<span className="tool-call-result">
								{tc.resultSummary || tc.result}
							</span>
						)}
					</div>
				))}
			</div>
		</div>
	);
}

ToolCallsLog.propTypes = { toolCalls: PropTypes.array.isRequired };

/* ─── Step 4 — Assembly (raw JSON only) ─────────────────────── */

function Step4Section({ assemblyResponses, audiences, audienceNames }) {
	if (!assemblyResponses?.length) return null;

	return (
		<div className="step4-content">
			{assemblyResponses.map((item, i) => {
				const warnings = audiences?.[i]?.validation_warnings;
				const title = audienceNames?.[i] || `Assembly Response [${i}]`;
				return (
					<Step4Audience
						key={i}
						title={title}
						data={item}
						warnings={warnings}
					/>
				);
			})}
		</div>
	);
}

Step4Section.propTypes = {
	assemblyResponses: PropTypes.array,
	audiences: PropTypes.array,
	audienceNames: PropTypes.array,
};

function Step4Audience({ title, data, warnings }) {
	const [open, setOpen] = useState(false);
	const warningCount = warnings?.length || 0;

	return (
		<div className="debug-collapsible nested">
			<button className="debug-collapsible-header" onClick={() => setOpen((o) => !o)}>
				<span className={`chevron ${open ? "open" : ""}`}>&#9654;</span>
				{title}
				{warningCount > 0 && (
					<span className="section-count">{warningCount} warning{warningCount !== 1 ? "s" : ""}</span>
				)}
			</button>
			{open && (
				<div className="debug-section-body">
					{warningCount > 0 && (
						<div className="validation-warnings-box">
							<span className="warning-icon">&#9888;&#65039;</span>
							<ul className="validation-warnings">
								{warnings.map((w, j) => (
									<li key={j}>{w}</li>
								))}
							</ul>
						</div>
					)}
					<pre className="debug-json-block">{JSON.stringify(data, null, 2)}</pre>
				</div>
			)}
		</div>
	);
}

Step4Audience.propTypes = {
	title: PropTypes.string.isRequired,
	data: PropTypes.any,
	warnings: PropTypes.array,
};

function RawCollapsible({ title, data }) {
	const [open, setOpen] = useState(false);
	return (
		<div className="debug-collapsible nested">
			<button className="debug-collapsible-header" onClick={() => setOpen((o) => !o)}>
				<span className={`chevron ${open ? "open" : ""}`}>&#9654;</span>
				{title}
			</button>
			{open && (
				<pre className="debug-json-block">{JSON.stringify(data, null, 2)}</pre>
			)}
		</div>
	);
}

RawCollapsible.propTypes = {
	title: PropTypes.string.isRequired,
	data: PropTypes.any,
};

/* ─── Helpers ───────────────────────────────────────────────── */

function formatRelevance(val) {
	if (val == null) return "";
	return `${(val * 100).toFixed(0)}%`;
}

function formatMs(ms) {
	if (ms == null) return "-";
	if (ms < 1000) return `${Math.round(ms)}ms`;
	if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
	const mins = Math.floor(ms / 60000);
	const secs = ((ms % 60000) / 1000).toFixed(1);
	return `${mins}m ${secs}s`;
}

function formatModelStep(key) {
	const map = {
		extract_strategy: "Extract Strategy",
		plan_audiences: "Plan Audiences",
		build_criteria: "Build Criteria",
		assemble_audience: "Assemble",
		label_audiences: "Label Audiences",
	};
	return map[key] || key;
}

// `models` reports fallback chains (string[]); `models_used.assemble_audience`
// is a per-audience array. Single strings render as-is.
function formatModelValue(val) {
	if (Array.isArray(val)) {
		if (val.length === 0) return "—";
		const unique = Array.from(new Set(val));
		return unique.length === 1 ? unique[0] : val.join(" → ");
	}
	return val ?? "";
}

function formatNumber(val) {
	if (val == null || Number.isNaN(val)) return "—";
	return Number(val).toLocaleString();
}

/* ─── Sized Pipeline Section ────────────────────────────────── */

function SizedPipelineSection({ sized, audienceNames }) {
	useRegisterRaw(sized);
	if (!sized) return null;

	const optimization = sized.optimization || [];
	const diversification = sized.diversification || [];
	const fallback = sized.fallback;

	const nameFor = (idx, fallbackName) =>
		fallbackName || audienceNames?.[idx] || `Audience ${idx + 1}`;

	return (
		<div className="sized-pipeline-content">
			{fallback && (
				<div className="validation-warnings-box">
					<span className="warning-icon">&#9888;&#65039;</span>
					<div className="sized-fallback-body">
						<strong>Fallback path used</strong>
						<p className="sized-fallback-reason">
							{fallback.reason}
							{fallback.details ? ` — ${fallback.details}` : ""}
						</p>
					</div>
				</div>
			)}

			<div className="config-grid">
				{sized.scale_floor != null && (
					<div className="config-row">
						<span className="config-label">Scale Floor</span>
						<span className="config-value mono">{formatNumber(sized.scale_floor)}</span>
					</div>
				)}
				{sized.jaccard_ceiling != null && (
					<div className="config-row">
						<span className="config-label">Jaccard Ceiling</span>
						<span className="config-value mono">{sized.jaccard_ceiling}</span>
					</div>
				)}
			</div>

			{optimization.length > 0 && (
				<div className="criteria-subsection">
					<h5>Optimization ({optimization.length})</h5>
					<table className="criteria-table">
						<thead>
							<tr>
								<th>Audience</th>
								<th>Status</th>
								<th>Initial</th>
								<th>Final</th>
								<th>Lower 2σ</th>
								<th>Dropped Groups</th>
							</tr>
						</thead>
						<tbody>
							{optimization.map((o, i) => (
								<tr key={i}>
									<td>{nameFor(o.audienceIndex, o.name)}</td>
									<td>
										<span
											className={`tag-pill small ${
												o.status === "optimized" ? "strategy" : "demo"
											}`}
										>
											{o.status}
										</span>
									</td>
									<td className="mono">{formatNumber(o.initialEstimate)}</td>
									<td className="mono">{formatNumber(o.finalEstimate)}</td>
									<td className="mono">{formatNumber(o.finalLower2sd)}</td>
									<td>
										{o.droppedGroups?.length > 0 ? (
											<div className="tag-list small">
												{o.droppedGroups.map((g, j) => (
													<span className="tag-pill small" key={j}>
														{g}
													</span>
												))}
											</div>
										) : (
											"—"
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{diversification.length > 0 && (
				<div className="criteria-subsection">
					<h5>Diversification ({diversification.length})</h5>
					<table className="criteria-table">
						<thead>
							<tr>
								<th>Audience</th>
								<th>Status</th>
								<th>Conflicts With</th>
								<th>Jaccard</th>
							</tr>
						</thead>
						<tbody>
							{diversification.map((d, i) => (
								<tr key={i}>
									<td>{nameFor(d.audienceIndex)}</td>
									<td>
										<span
											className={`tag-pill small ${
												d.status === "kept" ? "strategy" : "demo"
											}`}
										>
											{d.status}
										</span>
									</td>
									<td>
										{d.conflictsWith != null ? nameFor(d.conflictsWith) : "—"}
									</td>
									<td className="mono">
										{d.conflictJaccard != null
											? d.conflictJaccard.toFixed(3)
											: "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

SizedPipelineSection.propTypes = {
	sized: PropTypes.object,
	audienceNames: PropTypes.array,
};

/* ─── Dropped Audiences ─────────────────────────────────────── */

function DroppedAudiencesSection({ dropped }) {
	useRegisterRaw(dropped);
	if (!dropped?.length) return null;

	return (
		<table className="criteria-table">
			<thead>
				<tr>
					<th>Step</th>
					<th>Audience</th>
					<th>Error</th>
				</tr>
			</thead>
			<tbody>
				{dropped.map((d, i) => (
					<tr key={i}>
						<td className="mono">{d.step}</td>
						<td>{d.name}</td>
						<td>{d.error}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

DroppedAudiencesSection.propTypes = { dropped: PropTypes.array };

/* ─── Main DebugPanel ───────────────────────────────────────── */

function DebugPanel({ debugInfo, audiences }) {
	if (!debugInfo) return null;

	const audienceNames =
		debugInfo.step2_audience_plan?.audiences?.map((a) => a.name) || [];

	return (
		<div className="debug-panel">
			{debugInfo.timing && (
				<Section title="Pipeline Timing" sectionKey="timing" defaultOpen={true}>
					<TimingSection timing={debugInfo.timing} />
				</Section>
			)}

			{debugInfo.request_info && (
				<Section title="Request Config" sectionKey="config">
					<RequestConfigSection config={debugInfo.request_info} />
				</Section>
			)}

			{debugInfo.step1_extraction_response && (
				<Section title="Step 1 — Strategy Extraction" sectionKey="step1">
					<Step1Section
						extraction={debugInfo.step1_extraction_response}
						prompt={debugInfo.step1_extraction_prompt}
					/>
				</Section>
			)}

			{debugInfo.step2_audience_plan && (
				<Section title="Step 2 — Audience Plan" sectionKey="step2">
					<Step2Section plan={debugInfo.step2_audience_plan} />
				</Section>
			)}

			{debugInfo.step3_criteria_data?.length > 0 && (
				<Section
					title="Step 3 — Criteria Data"
					sectionKey="step3"
					count={debugInfo.step3_criteria_data.length}
				>
					<Step3Section
						criteriaData={debugInfo.step3_criteria_data}
						audienceNames={audienceNames}
					/>
				</Section>
			)}

			{debugInfo.step4_assembly_responses?.length > 0 && (
				<Section title="Step 4 — Assembly" sectionKey="step4">
					<Step4Section
						assemblyResponses={debugInfo.step4_assembly_responses}
						audiences={audiences}
						audienceNames={audienceNames}
					/>
				</Section>
			)}

			{debugInfo.sized_pipeline && (
				<Section title="Sized Pipeline" sectionKey="sized_pipeline">
					<SizedPipelineSection
						sized={debugInfo.sized_pipeline}
						audienceNames={audienceNames}
					/>
				</Section>
			)}

			{debugInfo.dropped_audiences?.length > 0 && (
				<Section
					title="Dropped Audiences"
					sectionKey="dropped_audiences"
					count={debugInfo.dropped_audiences.length}
					defaultOpen={true}
				>
					<DroppedAudiencesSection dropped={debugInfo.dropped_audiences} />
				</Section>
			)}
		</div>
	);
}

DebugPanel.propTypes = {
	debugInfo: PropTypes.object.isRequired,
	audiences: PropTypes.array,
};

export default DebugPanel;
